/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { observable, action, computed, reaction } from "mobx";
import { VerbIdProps, quickToolsVerbIdsForCommenting } from "constants/enums";
import { updateComponentProperties } from "utils/APIUtils";
import { getShouldShowCommentingTools } from "selectors/documentSelectors";
import { previewEvents } from "publicApis/Events/previewEvents";
import { eventNames } from "publicApis/constants.js";

class QuickToolsStore {
  @observable activeVerb;
  @observable componentProperties;
  @observable verbGroupState;
  @observable quickToolsVerbs;
  @observable groupIndexToShowFlyout;
  @observable isFocusOverQuickTools;
  @observable showPrimaryPanel;
  @observable isClickedByMouse;
  @observable selectedVerbOnUi;
  @observable shouldShowConditionalToolTip;
  @observable tooltipConfig;

  constructor() {
    this.init();
  }

  initStore(docApi, stores) {
    if (docApi) {
      this.docApi = docApi;
    }
    if (stores) {
      this.stores = stores;
    }
  }

  @action
  init(switchPreview = false) {
    this.activeVerb = "";
    this.selectedVerbOnUi = "";
    if (!switchPreview) {
      this.componentProperties = new Map();
      Object.values(VerbIdProps).forEach(verb =>
        this.componentProperties.set(verb, {
          visible: true,
          disable: undefined,
          notificationMsg: null,
          notificationOpts: null,
          disabledTooltipStr: null
        })
      );
    }
    this.verbGroupState = new Array(6);
    this.quickToolsVerbs = {};
    this.groupIndexToShowFlyout = -1;
    this.isFocusOverQuickTools = false;
    this.isClickedByMouse = false;
    reaction(
      () => this.disabledVerbs,
      () => {
        this.updateVerbsToShow();
      }
    );
    this.showPrimaryPanel = true;
  }

  @action
  setPrimaryPanelVisibility(value) {
    this.showPrimaryPanel = value;
  }

  @action
  setIsFocusOverQuickTool(value) {
    this.isFocusOverQuickTools = value;
  }

  @action
  setIsClickedByMouse(value) {
    this.isClickedByMouse = value;
  }

  @action
  setVerbGroupState(value) {
    this.verbGroupState = value;
  }

  @action
  setQuickToolsVerbs(value) {
    this.quickToolsVerbs = value;
  }

  @action
  setShouldShowConditionalToolTip(value) {
    this.shouldShowConditionalToolTip = value;
  }

  @action
  setTooltipConfig(verb, tooltipInfo) {
    const { message, variant, isControlled } = tooltipInfo;
    this.updatePropertyForVerb(verb, "tooltip", message);
    this.updatePropertyForVerb(verb, "tooltipVariant", variant);
    this.updatePropertyForVerb(verb, "isControlled", isControlled);
    this.setShouldShowConditionalToolTip(true);
  }

  @action
  resetTooltipConfig(verb, opts = {}) {
    this.setShouldShowConditionalToolTip(false);
    this.updatePropertyForVerb(verb, "tooltip", opts.defaultTooltipMsg);
    this.updatePropertyForVerb(verb, "tooltipVariant", undefined);
    this.updatePropertyForVerb(verb, "isControlled", undefined);
  }

  @action.bound
  updateVerbGroup(verb, groupIndex) {
    if (this.verbGroupState[groupIndex]) {
      if (this.verbGroupState[groupIndex].verbs) {
        this.verbGroupState[groupIndex].verbs.push(verb);
      } else {
        Object.assign(this.verbGroupState[groupIndex], { verbs: [verb] });
      }
    } else {
      this.verbGroupState[groupIndex] = {
        verbs: [verb]
      };
    }
    this.verbGroupState = [...this.verbGroupState];
  }

  @action.bound
  updatePropertyForVerbGroup(groupIndex, property = "", value = "") {
    if (this.verbGroupState[groupIndex]) {
      this.verbGroupState[groupIndex][property] = value;
    } else {
      this.verbGroupState[groupIndex] = {
        [property]: value
      };
    }
    this.verbGroupState = [...this.verbGroupState];
  }

  @action
  updateActiveVerbInGroup(verb, groupIndex, setDefaultActiveVerb = true) {
    const verbGroup = this.verbGroupState[groupIndex];
    const { currentActiveVerb, verbs } = verbGroup;
    // If there's no active verb yet, incoming verb becomes active
    if (!currentActiveVerb) {
      if (groupIndex === 0 && setDefaultActiveVerb) {
        this.setActiveVerb(verb);
      } else {
        this.updatePropertyForVerbGroup(groupIndex, "currentActiveVerb", verb);
      }
    } else {
      // Find index of incoming verb and compare with existing active verb
      const currentActiveVerbIndex = verbs.indexOf(currentActiveVerb);
      const incomingVerbIndex = verbs.indexOf(verb);
      if (incomingVerbIndex === 0 && groupIndex === 0) {
        this.setActiveVerb(verb);
      }
      if (incomingVerbIndex < currentActiveVerbIndex && this.quickToolsVerbs[verb].show) {
        this.updatePropertyForVerbGroup(groupIndex, "currentActiveVerb", verb);
      }
    }
  }

  @action
  updateVerbsToShow() {
    if (this.disabledVerbs.length > 0) {
      this.disabledVerbs.forEach(verb => {
        this.updatePropertyForVerb(verb, "show", false);
        this.updatePropertyForVerb(verb, "initialized", false);
        // Get groupIndex of the verb
        const groupIndex = this.quickToolsVerbs[verb].verbGroupIndex;
        if (groupIndex && groupIndex !== -1) {
          const verbGroup = this.verbGroupState[groupIndex];
          const { verbs: qtVerbs } = verbGroup;
          const firstValidVerb = qtVerbs.find(qtVerb => this.quickToolsVerbs[qtVerb].show && qtVerb !== verb);
          this.updatePropertyForVerbGroup(groupIndex, "currentActiveVerb", firstValidVerb);
        }
      });
    }
  }

  @action.bound
  updatePropertyForVerb(verb = "", property = "", value) {
    const quickToolsVerbs = {
      ...this.quickToolsVerbs,
      [verb]: {
        ...this.quickToolsVerbs[verb],
        [property]: value
      }
    };
    this.setQuickToolsVerbs(quickToolsVerbs);
  }

  @action.bound
  updatePropertiesForAllVerbs(properties = {}) {
    for (const [key, value] of this.componentProperties) {
      updateComponentProperties(this.componentProperties, key, properties);
    }
  }

  @action.bound
  addVerbToQuickTools(verbDetail, groupIndex) {
    const { label, tooltip, ariaLabel, icon, verbId } = verbDetail;
    const qtVerbObj = {
      [verbId]: {
        label,
        tooltip,
        ariaLabel,
        icon,
        verbGroupIndex: groupIndex,
        verbId
      }
    };
    const quickToolsVerbs = { ...this.quickToolsVerbs, ...qtVerbObj };
    this.setQuickToolsVerbs(quickToolsVerbs);
  }

  @action.bound
  setGroupIndexToShowFlyout(value) {
    this.groupIndexToShowFlyout = value;
  }

  @action.bound
  resetFlyoutIndex() {
    this.groupIndexToShowFlyout = -1;
  }

  @action.bound
  setComponentProperties(propName, config) {
    if (this.activeVerb === VerbIdProps[propName] && !config.visible)
      throw new Error("Selected tool can not be customised");
    if (quickToolsVerbIdsForCommenting.includes(VerbIdProps[propName]) && !getShouldShowCommentingTools(this.stores))
      throw new Error("Commenting is not enabled in this document.");
    updateComponentProperties(this.componentProperties, VerbIdProps[propName], config);
  }

  @computed
  get disabledVerbs() {
    const props = [];
    for (const [key, value] of this.componentProperties) {
      if (!value.visible) props.push(key);
    }
    return props;
  }

  @computed
  get filteredQTGroups() {
    return this.verbGroupState.filter(group => {
      if (group) {
        const { verbs, currentActiveVerb, isValid } = group;
        if (!isValid || !currentActiveVerb) {
          return false;
        }
        if (
          verbs.every(
            verb =>
              (this.quickToolsVerbs[verb] && !this.quickToolsVerbs[verb].show) || this.disabledVerbs.includes(verb)
          )
        ) {
          return false;
        }
        return true;
      }
      return false;
    });
  }

  @action
  setActiveVerb(verb = "", opts = {}) {
    const { shouldSelectVerbOnClick = true } = opts;
    if (verb) {
      previewEvents.trigger(eventNames.QUICK_TOOL_VERB_CHANGE, this.activeVerb, verb, opts);
      this.activeVerb = verb;
      if (verb in this.quickToolsVerbs) {
        // Find which group verb relates to
        const groupIndex = this.quickToolsVerbs[verb].verbGroupIndex;
        if (shouldSelectVerbOnClick) {
          this.updatePropertyForVerbGroup(groupIndex, "currentActiveVerb", verb);
          this.selectedVerbOnUi = verb;
        }
      }
    }
  }

  @action
  resetQuickTools(opts = {}) {
    const { shouldRetainGroupSelectedState } = opts;
    this.verbGroupState.forEach((verbGroup, index) => {
      if (verbGroup && verbGroup.isValid) {
        if (!shouldRetainGroupSelectedState) {
          const { verbs, currentActiveVerb } = verbGroup;
          const firstValidVerb = verbs.find(
            verb => this.quickToolsVerbs[verb].show && !this.disabledVerbs.includes(verb)
          );
          if (index === 0) {
            previewEvents.trigger(eventNames.QUICK_TOOL_VERB_CHANGE, this.activeVerb, firstValidVerb);
            this.activeVerb = firstValidVerb;
            this.selectedVerbOnUi = firstValidVerb;
          }
          if (currentActiveVerb !== firstValidVerb) {
            this.updatePropertyForVerbGroup(index, "currentActiveVerb", firstValidVerb);
          }
        }
        this.stores.documentStore.deRegisterSecondaryComponent();
      }
    });
  }

  @action
  resetActiveVerb() {
    previewEvents.trigger(eventNames.QUICK_TOOL_VERB_CHANGE, this.activeVerb, "");
    this.activeVerb = "";
    this.selectedVerbOnUi = "";
    this.groupIndexToShowFlyout = -1;
  }
}

export { QuickToolsStore };
